import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Switch,
} from '@mui/material';
import { Add, Edit, Delete, Palette, CheckCircleOutline, CloseOutlined, VisibilityOutlined, AccountBalance } from '@mui/icons-material';
import { ColorPicker } from '../../../components/ColorPicker';
import { UserSelector } from '../../../components/UserSelector';
import { ITheme, IThemeColorSet, IAccount } from '../../../resource/interfaces';
import { generateColorSet, isValidHexColor, hexToRgb, rgbToHex } from '../../../utils/colorUtils';
import apis from '../../../apis';
import { useToastContext } from '../../../components/Toast/toastContext';
import { useAlertContext } from '../../../components/Alert/alertContext';
import { AppContext } from '../../../context';
import styles from '../styles';

const DEBOUNCE_DELAY = 500; // 500ms

const ThemeSettings: React.FC = () => {
  const { activeToast } = useToastContext();
  const { activeAlert } = useAlertContext();
  const { isAdmin, account } = useContext(AppContext);
  const [themes, setThemes] = useState<ITheme[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTheme, setEditingTheme] = useState<ITheme | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    mainColor: '#1976d2',
    colorSet: generateColorSet('#1976d2'),
    fontFamily: '',
    fontUrl: '',
    fontFaceFamily: '',
  });
  const [gradientColors, setGradientColors] = useState<string[]>(['#1976d2']);
  const [pendingUserAssignments, setPendingUserAssignments] = useState<Map<string, NodeJS.Timeout>>(new Map());
  
  // Configuration for debounce delay

  /** First family token from a CSS font-family stack (strips quotes). */
  const primaryFontFaceName = (cssFamily: string) => {
    const first = (cssFamily || '').split(',')[0]?.trim() || '';
    return first.replace(/^['"]|['"]$/g, '');
  };

  useEffect(() => {
    loadThemes();
  }, [isAdmin]);

  // Cleanup pending timeouts on unmount
  useEffect(() => {
    return () => {
      pendingUserAssignments.forEach(timeout => clearTimeout(timeout));
    };
  }, [pendingUserAssignments]);

  const loadThemes = async () => {
    setLoading(true);
    try {
      console.log('loading themes');
      // For demo purposes, create some sample themes if API is not available
      if (!apis.theme || typeof apis.theme.getThemes !== 'function') {
        // Demo data
        const demoThemes: ITheme[] = [
          {
            _id: '1',
            name: 'Default Blue',
            mainColor: '#1976d2',
            colorSet: generateColorSet('#1976d2'),
            isActive: true,
            users: [] as IAccount[], // Multiple users can be assigned
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
          {
            _id: '2',
            name: 'Forest Green',
            mainColor: '#2e7d32',
            colorSet: generateColorSet('#2e7d32'),
            isActive: false,
            users: [] as IAccount[], // Multiple users can be assigned
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
          {
            _id: '3',
            name: 'Sunset Orange',
            mainColor: '#ed6c02',
            colorSet: generateColorSet('#ed6c02'),
            isActive: false,
            users: [] as IAccount[], // Multiple users can be assigned
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
        ];
        setThemes(demoThemes);
        return;
      }

      const result = await apis.theme.getThemes();
      if (result.success) {
        // Ensure backward compatibility for themes without gradient colors and users array
        const themesWithGradients = result.themes.map(theme => ({
          ...theme,
          colorSet: {
            ...theme.colorSet,
            gradientColors: theme.colorSet.gradientColors || generateColorSet(theme.mainColor).gradientColors
          },
          users: theme.users as IAccount[] // Convert old user field to users array
        }));
        setThemes(themesWithGradients);
      } else {
        activeToast({ type: 'error', text: result.msg || 'Failed to load themes' });
      }
    } catch (error) {
      activeToast({ type: 'error', text: 'Failed to load themes' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (theme?: ITheme) => {
    if (theme) {
      setEditingTheme(theme);
      const firstUrl = theme.fontUrls?.[0];
      setFormData({
        name: theme.name,
        mainColor: theme.mainColor,
        colorSet: theme.colorSet,
        fontFamily: theme.fontFamily || '',
        fontUrl: firstUrl?.url || '',
        fontFaceFamily: firstUrl?.family || primaryFontFaceName(theme.fontFamily || ''),
      });
      // Initialize gradient colors from theme or fallback to main color
      const existingGradients = theme.colorSet.gradientColors || generateColorSet(theme.mainColor).gradientColors;
      setGradientColors(existingGradients);
    } else {
      setEditingTheme(null);
      setFormData({
        name: '',
        mainColor: '#1976d2',
        colorSet: generateColorSet('#1976d2'),
        fontFamily: '',
        fontUrl: '',
        fontFaceFamily: '',
      });
      setGradientColors(['#1976d2']);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTheme(null);
    setFormData({
      name: '',
      mainColor: '#1976d2',
      colorSet: generateColorSet('#1976d2'),
      fontFamily: '',
      fontUrl: '',
      fontFaceFamily: '',
    });
    setGradientColors(['#1976d2']);
  };

  const handleFormChange = (field: string, value: string) => {
    if (field === 'mainColor') {
      setFormData(prev => ({ 
        ...prev, 
        [field]: value,
        // Don't auto-update colorSet here - only when button is clicked
      }));
      // Reset gradient colors when main color changes
      setGradientColors([value]);
      // Also update the colorSet gradient colors
      setFormData(prev => ({
        ...prev,
        colorSet: {
          ...prev.colorSet,
          gradientColors: [value]
        }
      }));
    } else if (field.startsWith('colorSet.')) {
      const colorKey = field.replace('colorSet.', '');
      setFormData(prev => ({
        ...prev,
        colorSet: {
          ...prev.colorSet,
          [colorKey]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleUserAssignment = async (themeId: string, users: IAccount[]) => {
    if (!users || users.length === 0) return;
    
    // Store the previous state for rollback if needed
    const previousThemes = themes;
    
    // Optimistic update - update the UI immediately
    setThemes(prev => prev.map(t => 
      t._id === themeId ? { ...t, users: users } : t
    ));
    
    try {
      const result = await apis.theme.updateTheme(themeId!, { users: users.map((user) => user.id) });
      if (result.success) {
        // API call successful, no need to update again
        activeToast({ type: 'success', text: 'Theme assigned to users successfully' });
      } else {
        // API call failed, rollback to previous state
        setThemes(previousThemes);
        activeToast({ type: 'error', text: result.msg || 'Failed to assign theme to users' });
      }
    } catch (error) {
      // API call failed, rollback to previous state
      setThemes(previousThemes);
      activeToast({ type: 'error', text: 'Failed to assign theme to users' });
    }
  };

  // Debounced user assignment function
  const debouncedUserAssignment = useCallback((themeId: string, users: IAccount[]) => {
    // Clear any existing timeout for this theme
    const existingTimeout = pendingUserAssignments.get(themeId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Store the previous state for rollback if needed
    const previousThemes = themes;
    
    // Optimistic update - update the UI immediately
    setThemes(prev => prev.map(t => 
      t._id === themeId ? { ...t, users: users } : t
    ));

    // Create a new timeout for the API call
    const timeoutId = setTimeout(async () => {
      try {
        const result = await apis.theme.updateTheme(themeId, { users: users.map((user) => user.id) });
        if (result.success) {
          // API call successful, no need to update again
          activeToast({ type: 'success', text: 'Theme assigned to users successfully' });
        } else {
          // API call failed, rollback to previous state
          setThemes(previousThemes);
          activeToast({ type: 'error', text: result.msg || 'Failed to assign theme to users' });
        }
      } catch (error) {
        // API call failed, rollback to previous state
        setThemes(previousThemes);
        activeToast({ type: 'error', text: 'Failed to assign theme to users' });
      }
      
      // Remove the timeout from pending assignments
      setPendingUserAssignments(prev => {
        const newMap = new Map(prev);
        newMap.delete(themeId);
        return newMap;
      });
    }, DEBOUNCE_DELAY); // Configurable debounce delay

    // Store the new timeout
    setPendingUserAssignments(prev => new Map(prev).set(themeId, timeoutId));
  }, [themes, pendingUserAssignments, activeToast]);

  // Immediate user assignment function (bypasses debounce)
  const immediateUserAssignment = useCallback(async (themeId: string, users: IAccount[]) => {
    // Clear any pending debounced assignment
    const existingTimeout = pendingUserAssignments.get(themeId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      setPendingUserAssignments(prev => {
        const newMap = new Map(prev);
        newMap.delete(themeId);
        return newMap;
      });
    }

    // Use the original handleUserAssignment for immediate API call
    await handleUserAssignment(themeId, users);
  }, [pendingUserAssignments, handleUserAssignment]);

  const handleGradientColorChange = (index: number, color: string) => {
    setGradientColors(prev => {
      const newColors = [...prev];
      newColors[index] = color;
      return newColors;
    });
    
    // Also update the colorSet to keep them in sync
    setFormData(prev => ({
      ...prev,
      colorSet: {
        ...prev.colorSet,
        gradientColors: [...gradientColors.slice(0, index), color, ...gradientColors.slice(index + 1)]
      }
    }));
  };

  const handleAddGradientColor = () => {
    setGradientColors(prev => {
      const newColors = [...prev, '#cccccc'];
      // Also update the colorSet to keep them in sync
      setFormData(prevForm => ({
        ...prevForm,
        colorSet: {
          ...prevForm.colorSet,
          gradientColors: newColors
        }
      }));
      return newColors;
    });
  };

  const handleRemoveGradientColor = (index: number) => {
    if (gradientColors.length > 1) {
      setGradientColors(prev => {
        const newColors = prev.filter((_, i) => i !== index);
        // Also update the colorSet to keep them in sync
        setFormData(prevForm => ({
          ...prevForm,
          colorSet: {
            ...prevForm.colorSet,
            gradientColors: newColors
          }
        }));
        return newColors;
      });
    } else {
      activeToast({ type: 'warning', text: 'At least one gradient color is required' });
    }
  };

  const handleGenerateColors = () => {
    if (!isValidHexColor(formData.mainColor)) {
      activeToast({ type: 'error', text: 'Please enter a valid hex color' });
      return;
    }
    
    const newColorSet = generateColorSet(formData.mainColor);
    
    setFormData(prev => ({
      ...prev,
      colorSet: newColorSet
    }));

    // Update gradient colors from the generated color set
    setGradientColors(newColorSet.gradientColors);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      activeToast({ type: 'error', text: 'Theme name is required' });
      return;
    }

    if (!isValidHexColor(formData.mainColor)) {
      activeToast({ type: 'error', text: 'Please enter a valid hex color' });
      return;
    }

    setSaving(true);
    try {
      // Create color set with gradient colors included
      const colorSetWithGradients = {
        ...formData.colorSet,
        gradientColors: gradientColors
      };

      const fontFamily = formData.fontFamily.trim();
      const fontUrl = formData.fontUrl.trim();
      const fontFaceFamily =
        formData.fontFaceFamily.trim() || primaryFontFaceName(fontFamily) || '';

      const themeData = {
        name: formData.name.trim(),
        mainColor: formData.mainColor,
        colorSet: colorSetWithGradients,
        fontFamily,
        fontUrls:
          fontUrl && fontFaceFamily
            ? [{ family: fontFaceFamily, url: fontUrl, weight: 'normal', style: 'normal' }]
            : [],
      };

      // For demo purposes, handle locally if API is not available
      if (!apis.theme || typeof apis.theme.createTheme !== 'function') {
        if (editingTheme) {
          // Update existing theme
          setThemes(prev => prev.map(t => 
            t._id === editingTheme._id 
              ? { ...t, ...themeData, users: [] as IAccount[], updatedAt: new Date().toISOString() }
              : t
          ));
        } else {
          // Create new theme
          const newTheme: ITheme = {
            _id: Date.now().toString(),
            ...themeData,
            isActive: false,
            users: [] as IAccount[], // Initialize with empty users array
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          setThemes(prev => [...prev, newTheme]);
        }
        
        activeToast({ 
          type: 'success', 
          text: `Theme ${editingTheme ? 'updated' : 'created'} successfully! (Demo mode)` 
        });
        handleCloseDialog();
        setSaving(false);
        return;
      }

      let result;
      if (editingTheme) {
        result = await apis.theme.updateTheme(editingTheme._id!, themeData);
      } else {
        result = await apis.theme.createTheme(themeData);
      }

      if (result.success) {
        activeToast({ 
          type: 'success', 
          text: `Theme ${editingTheme ? 'updated' : 'created'} successfully!` 
        });
        handleCloseDialog();
        loadThemes();
      } else {
        activeToast({ type: 'error', text: result.msg || 'Failed to save theme' });
      }
    } catch (error) {
      activeToast({ type: 'error', text: 'Failed to save theme' });
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefault = async (themeId: string, checked: boolean) => {
    try {
      setThemes(prev => prev.map(t => ({ ...t, isDefault: t._id === themeId ? checked : t.isDefault })));
      const result = await apis.theme.updateTheme(themeId, { isDefault: checked });
    } catch (error) {
      activeToast({ type: 'error', text: 'Failed to set default theme' });
    }
  };

  const handleDelete = async (theme: ITheme) => {
    activeAlert({
      type: 'info',
      text: `Are you sure you want to delete "${theme.name}"?`,
    }, async () => {
      try {
        // For demo purposes, handle locally if API is not available
        if (!apis.theme || typeof apis.theme.deleteTheme !== 'function') {
          setThemes(prev => prev.filter(t => t._id !== theme._id));
          activeToast({ type: 'success', text: 'Theme deleted successfully! (Demo mode)' });
          return;
        }

        const result = await apis.theme.deleteTheme(theme._id!);
        if (result.success) {
          activeToast({ type: 'success', text: 'Theme deleted successfully!' });
          loadThemes();
        } else {
          activeToast({ type: 'error', text: result.msg || 'Failed to delete theme' });
        }
      } catch (error) {
        activeToast({ type: 'error', text: 'Failed to delete theme' });
      }
    });
  };

  const canEditTheme = useCallback((theme: ITheme) => {
    return isAdmin || theme.users?.some((user) => typeof user === 'object' && user?.id === account?.id);
  }, [isAdmin, account]);

  const ColorPreview: React.FC<{ color: string; label: string }> = ({ color, label }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <Box
        sx={{
          width: 20,
          height: 20,
          backgroundColor: color,
          border: '1px solid #ddd',
          borderRadius: 1,
        }}
      />
      <Typography variant="caption" sx={{ minWidth: 80 }}>
        {label}:
      </Typography>
      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
        {color}
      </Typography>
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6">Theme Management</Typography>
          {(!apis.theme || typeof apis.theme.getThemes !== 'function') && (
            <Typography variant="caption" color="text.secondary">
              Demo mode - changes are stored locally
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add Theme
        </Button>
      </Box>

      <Table sx={styles.table}>
        <TableHead>
          <TableRow>
            <TableCell sx={styles.th}>Theme Name</TableCell>
            <TableCell sx={styles.th}>Main Color</TableCell>
            <TableCell sx={styles.th}>Color Set Preview</TableCell>
            {isAdmin && <TableCell sx={styles.th}>Assigned Users (Multiple)</TableCell>}
            <TableCell sx={styles.th}>Default</TableCell>
            <TableCell sx={styles.th}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {themes.map((theme) => (
            <TableRow key={theme._id} sx={styles.tr}>
              <TableCell sx={styles.td}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Palette sx={{ color: theme.mainColor }} />
                  <Typography variant="body1" fontWeight={500}>
                    {theme.name}
                  </Typography>
                </Box>
              </TableCell>
              
              <TableCell sx={styles.td}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      backgroundColor: theme.mainColor,
                      border: '1px solid #ddd',
                      borderRadius: 1,
                    }}
                  />
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {theme.mainColor}
                  </Typography>
                </Box>
              </TableCell>
              
              <TableCell sx={styles.td}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {Object.entries(theme.colorSet).slice(0, 6).map(([key, color]) => (
                    <Box
                      key={key}
                      sx={{
                        width: 16,
                        height: 16,
                        backgroundColor: color,
                        border: '1px solid #ddd',
                        borderRadius: 0.5,
                      }}
                      title={`${key}: ${color}`}
                    />
                  ))}
                </Box>
              </TableCell>
              
              {isAdmin && (
                <TableCell sx={styles.td}>
                  <Box sx={{ maxWidth: 300 }}>
                    <UserSelector
                      multiple={true}
                      value={theme.users?.map((user) => typeof user === 'string' ? user : user.id) || []}
                      onChange={(users) => {
                        const userArray = Array.isArray(users) ? users : (users ? [users] : []);
                        debouncedUserAssignment(theme._id || '', userArray);
                      }}
                      includeMe={false}
                      loading={pendingUserAssignments.has(theme._id || '')}
                    />
                  </Box>
                </TableCell>
               )}

              <TableCell>
               {isAdmin ? (
                  <Switch
                    color="secondary"
                    checked={theme.isDefault}
                    onChange={(e) => handleSetDefault(theme._id || '', e.target.checked)}
                  />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {theme.isDefault ? <CheckCircleOutline sx={{ color: 'success.main' }}  /> : <CloseOutlined />}
                  </Box>
                )}
               </TableCell>
              
              <TableCell sx={styles.td}>
                <Box display="flex" gap={8}>
                  <IconButton 
                    size="medium" 
                    onClick={() => handleOpenDialog(theme)}
                  >
                    {canEditTheme(theme) ? <Edit /> : <VisibilityOutlined />}
                  </IconButton>
                  
                  {canEditTheme(theme) && (
                    <Tooltip title="Delete Theme">
                    <IconButton 
                      size="medium" 
                      onClick={() => handleDelete(theme)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {themes.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No themes found. Create your first theme to get started!
        </Alert>
      )}

      {/* Add/Edit Theme Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTheme ? canEditTheme(editingTheme) ? 'Edit Theme' : 'View Theme' : 'Add New Theme'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Theme Name"
              value={formData.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              sx={{ mb: 8 }}
              required
            />
            
            <Box sx={{ mb: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Main Color
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 8, mb: 2 }}>
                  {!editingTheme || canEditTheme(editingTheme) ? (
                    <ColorPicker 
                      color={formData.mainColor} 
                      onChange={(color) => handleFormChange('mainColor', color)} 
                      height={150}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: formData.mainColor,
                          border: '2px solid #ddd',
                          borderRadius: '50%',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            transform: 'scale(1.1)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                          }
                        }}
                      />
                    </ColorPicker>  
                  ) : (
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        backgroundColor: formData.mainColor,
                        border: '2px solid #ddd',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'scale(1.1)',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                        }
                      }}
                    />
                  )}
                  
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {formData.mainColor}
                  </Typography>
                </Box>
              </Box>
              
              {!editingTheme || canEditTheme(editingTheme) && (
                <Button
                  variant="outlined"
                  onClick={handleGenerateColors}
                  disabled={!isValidHexColor(formData.mainColor)}
                >
                  Generate Color Set
                </Button>
              )}
            </Box>

            <Box sx={{ mb: 8 }}>
              <Typography variant="subtitle2" gutterBottom>
                Color Set
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                {Object.entries(formData.colorSet)
                  .filter(([key]) => key !== 'gradientColors')
                  .map(([key, color]) => (
                    <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {!editingTheme || canEditTheme(editingTheme) ? (
                        <ColorPicker 
                          color={color} 
                          onChange={(colorObj) => handleFormChange(`colorSet.${key}`, colorObj)} 
                          height={150}
                        >
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              backgroundColor: color,
                              border: '1px solid #ddd',
                              borderRadius: '50%',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                transform: 'scale(1.1)',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                              }
                            }}
                          />
                        </ColorPicker>
                      ) : (
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            backgroundColor: color,
                            border: '1px solid #ddd',
                            borderRadius: '50%',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              transform: 'scale(1.1)',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                            }
                          }}
                        />
                      )}
                      <Typography variant="caption" sx={{ minWidth: 60, textTransform: 'capitalize' }}>
                        {key}:
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1 }}>
                        {color}
                      </Typography>
                    </Box>
                ))}
              </Box>
            </Box>

            <Box sx={{ mb: 8 }}>
              <Typography variant="subtitle2" gutterBottom>
                Gradient Colors
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {gradientColors.map((color, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      gap: 1,
                      position: 'relative',
                    }}
                  >
                    {!editingTheme || canEditTheme(editingTheme) ? (
                      <ColorPicker 
                      color={color} 
                      onChange={(colorObj) => handleGradientColorChange(index, colorObj)}
                      height={150}
                    >
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          backgroundColor: color,
                          border: '1px solid #ddd',
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                          }
                        }}
                      >
                        <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>
                          {index + 1}
                        </Typography>
                      </Box>
                    </ColorPicker>
                    ) : (
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          backgroundColor: color,
                          border: '1px solid #ddd',
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                        }}
                      />
                    )}
                    
                    {!editingTheme || canEditTheme(editingTheme) && (
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveGradientColor(index)}
                        sx={{
                          width: 24,
                          height: 24,
                          backgroundColor: 'transparent',
                          boxShadow: 'none',
                          color: 'text.main',
                          border: 'transparent',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ))}
                
                {!editingTheme || canEditTheme(editingTheme) && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <Tooltip title="Add gradient color">
                      <Button
                        variant="outlined"
                        onClick={handleAddGradientColor}
                        sx={{
                          width: 48,
                          height: 48,
                          minWidth: 'unset',
                          borderRadius: 2,
                        }}
                      >
                        <Add />
                      </Button>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            </Box>

            <Box sx={{ mb: 8 }}>
              <Typography variant="subtitle2" gutterBottom>
                Widget font (CDN embeds)
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Leave empty to inherit the host page font (HubSpot). For iframe hosts (Wix), set a CSS
                family and a public font file URL so the widget can load @font-face inside its own document.
              </Typography>
              <TextField
                fullWidth
                label="Font family (CSS)"
                placeholder={`"Costa Pro", serif`}
                value={formData.fontFamily}
                onChange={(e) => handleFormChange('fontFamily', e.target.value)}
                sx={{ mb: 2 }}
                disabled={!!editingTheme && !canEditTheme(editingTheme)}
                helperText="Applied to .cdn-root. Empty = inherit."
              />
              <TextField
                fullWidth
                label="Font face name"
                placeholder="Costa Pro"
                value={formData.fontFaceFamily}
                onChange={(e) => handleFormChange('fontFaceFamily', e.target.value)}
                sx={{ mb: 2 }}
                disabled={!!editingTheme && !canEditTheme(editingTheme)}
                helperText="Name used in @font-face. Defaults to the first token of Font family."
              />
              <TextField
                fullWidth
                label="Font file URL (.woff2 / .woff)"
                placeholder="https://…/font.woff2"
                value={formData.fontUrl}
                onChange={(e) => handleFormChange('fontUrl', e.target.value)}
                disabled={!!editingTheme && !canEditTheme(editingTheme)}
                helperText="Required for Wix/iframe embeds. Must be HTTPS and CORS-accessible."
              />
            </Box>

            {isValidHexColor(formData.mainColor) && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Preview of Current Colors:
                </Typography>
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  {Object.entries(formData.colorSet).slice(0, 4).map(([key, color]) => (
                    <ColorPreview key={key} color={color} label={key} />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: 16 }}>
          <Button variant='outlined' onClick={handleCloseDialog}>Cancel</Button>
          {(!editingTheme || canEditTheme(editingTheme)) && (
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={saving || !formData.name.trim() || !isValidHexColor(formData.mainColor)}
            >
              {saving ? <CircularProgress size={20} /> : (editingTheme ? 'Update' : 'Create')}
            </Button>  
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ThemeSettings;
